import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../utils/seo';

export default function StandardsPage() {
  useEffect(() => {
    updateSEO(seoData['/standards'] ?? {
      title: '執事の実務基準｜一般社団法人 全日本執事協会',
      description:
        '全日本執事協会が定める、執事が現場で守る実務上の基準の全体像。倫理・行動・接遇・所作・守秘・危機管理など12の指針への導線ハブ。',
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事の実務基準' },
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const pillars = [
    {
      no: '01',
      en: 'Ethical Core',
      ja: '倫理と行動の核',
      desc: '現場判断の起点となる倫理綱領と行動規範。何を優先し、何をしないかを言語化し、属人的な勘に頼らない判断の土台をつくります。',
    },
    {
      no: '02',
      en: 'Service Presence',
      ja: '接遇・所作・身だしなみ',
      desc: '主人と来客の前に立つ姿そのものが品質です。言葉・間・立ち居振る舞い・装いを指針として整え、場の空気を乱さない奉仕を目指します。',
    },
    {
      no: '03',
      en: 'Stewardship',
      ja: '守秘・危機・情報・時間',
      desc: '家庭の内側を預かる職として、沈黙・緊急時の手順・情報の扱い・予定の設計を実務基準に落とし込み、継続可能な運営を支えます。',
    },
  ];

  const standards = [
    {
      no: '01',
      href: '/standards/ethics',
      en: 'Code of Ethics',
      ja: '執事倫理綱領',
      text: '奉仕の目的、誠実さ、境界意識など、判断の最上位に置く倫理の骨格を示します。',
    },
    {
      no: '02',
      href: '/standards/conduct',
      en: 'Code of Conduct',
      ja: '執事行動規範',
      text: '日常の振る舞い、報告・連絡、利害の扱いなど、行動として守るべき規範を整理します。',
    },
    {
      no: '03',
      href: '/standards/hospitality',
      en: 'Hospitality Guidelines',
      ja: '接遇指針',
      text: '迎賓から見送りまでの流れ、言葉遣い、過不足のないもてなしの設計を扱います。',
    },
    {
      no: '04',
      href: '/standards/deportment',
      en: 'Deportment',
      ja: '所作指針',
      text: '歩き方、立ち位置、物の扱いなど、静かな存在感を保つ身体運用の指針です。',
    },
    {
      no: '05',
      href: '/standards/grooming',
      en: 'Grooming',
      ja: '身だしなみ指針',
      text: '装い・清潔・香りなど、場を乱さない身だしなみの水準と確認の観点をまとめます。',
    },
    {
      no: '06',
      href: '/standards/confidentiality',
      en: 'Confidentiality',
      ja: '守秘指針',
      text: '見聞きした事柄の扱い、話題の境界、退任後を含む沈黙の責任を明確にします。',
    },
    {
      no: '07',
      href: '/standards/crisis',
      en: 'Crisis Response',
      ja: '危機管理指針',
      text: '事故・体調急変・災害など、緊急時の初動とエスカレーションの考え方を示します。',
    },
    {
      no: '08',
      href: '/standards/infosec',
      en: 'Information Security',
      ja: '情報セキュリティ指針',
      text: '書類・端末・通信・共有範囲など、家庭情報を守る実務上の取り扱いを定めます。',
    },
    {
      no: '09',
      href: '/standards/schedule',
      en: 'Schedule Stewardship',
      ja: '時間・予定管理指針',
      text: '予定の設計、余白の確保、変更への対応など、時間を預かる責任の指針です。',
    },
    {
      no: '10',
      href: '/standards/aptitude',
      en: 'Aptitude',
      ja: '執事に必要な素養',
      text: '観察眼、忍耐、学習姿勢など、技能以前に求められる素養の見取り図を示します。',
    },
    {
      no: '11',
      href: '/standards/certification',
      en: 'Certification Criteria',
      ja: '認定基準',
      text: '協会が到達を認める際の観点と、資格段階との対応関係を基準として整理します。',
    },
    {
      no: '12',
      href: '/standards/ai-role',
      en: 'Butler and AI',
      ja: '執事とAIの役割分担',
      text: '自動化できる手配と、人が担う判断・同席・信頼の領域を切り分けて考えます。',
    },
  ];

  const readingPaths = [
    {
      title: 'はじめて基準に触れる方',
      text: 'まず倫理綱領と行動規範で「何を大切にする職か」を掴み、続けて接遇・所作・身だしなみへ進むと、現場の姿が具体化しやすい構成です。職能の定義そのものは',
      linkHref: '/butler',
      linkLabel: '執事とは',
      suffix: 'の頁も併せて参照ください。',
    },
    {
      title: '現場ですでに奉仕に携わる方',
      text: '守秘・危機管理・情報セキュリティ・時間管理の各指針を優先し、自らの手順と照合することを推奨します。到達段階の確認は',
      linkHref: '/qualifications',
      linkLabel: '資格制度',
      suffix: 'と認定基準を突き合わせると整理が進みます。',
    },
    {
      title: '学びの道筋を設計したい方',
      text: '素養の頁で土台を確認したうえで、Associate・Professional・Masterの各段階に必要な基準を拾い読みする方法があります。なり方の全体像は',
      linkHref: '/butler/become',
      linkLabel: '執事になるには',
      suffix: 'もあわせてご覧ください。',
    },
  ];

  const faqJsonLd = [
    {
      question: '実務基準とは何ですか',
      answer:
        '当協会が認定団体として定める、執事が現場で守るべき実務上の考え方と手順の枠組みです。倫理・行動・接遇・所作・身だしなみ・守秘・危機管理・情報セキュリティ・時間管理・素養・認定・AIとの役割分担の十二領域で構成し、属人的な勘だけに頼らない品質の共通言語とすることを目的とします。',
    },
    {
      question: '資格制度と実務基準はどう関係しますか',
      answer:
        '資格はAssociate・Professional・Masterの三段階と専門資格として到達を示す枠組みであり、実務基準はその各段階で参照される判断と振る舞いの中身です。基準を学ぶことと、認定で到達を示すことは一体の関係にあります。',
    },
    {
      question: '十二の指針はすべて同時に満たす必要がありますか',
      answer:
        'すべてが職能の地図を構成しますが、学びの順序は一律ではありません。倫理と行動を起点に、担当する現場の性質に応じて接遇や守秘、危機管理などへ深める読み方を想定しています。認定基準の頁で、段階ごとの重心も整理します。',
    },
    {
      question: 'AIの活用は実務基準に含まれますか',
      answer:
        '含まれます。手配や情報整理など機械が担える領域と、同席・機微の判断・信頼関係など人が担う領域を切り分けることが、現代の家庭運営では不可欠だと考えます。詳細は「執事とAIの役割分担」の指針で扱います。',
    },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の実務基準｜現場で守る十二の指針',
    description:
      '一般社団法人全日本執事協会が定める、執事が現場で守る実務上の基準の全体像。倫理・行動・接遇・所作・守秘・危機管理など十二指針への導線。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会',
      foundingDate: '1890',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '青海2-7-4',
        addressLocality: '江東区',
        addressRegion: '東京都',
        addressCountry: 'JP',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会',
      identifier: '2010005039584',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards',
    },
    inLanguage: 'ja-JP',
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '執事の実務基準', item: 'https://japanbutlerassociation.com/standards' },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqJsonLd.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード型 */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <picture>
          <source srcSet="/images/generated/jba-butler-profession-hero.webp" type="image/webp" />
          <img
            src="/images/generated/jba-std-hub-01.jpg"
            alt="執事の実務基準を象徴する静謐な空間。品格ある奉仕の姿勢を示す場面"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </picture>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Professional Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の実務基準</span>
              <span className="block text-[#d7b779]">現場で守る十二の指針</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、認定団体として、執事が家庭と主人の内側で守るべき実務上の基準を定めます。本頁は倫理から接遇、守秘、危機管理、認定、AIとの役割分担に至る十二の指針への導線ハブです。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-16 sm:space-y-20">

          {/* S1. 実務基準の位置づけ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Framework</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              実務基準という<span className="text-[#a38e65]">共通言語</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                執事の仕事は、外部から成果を数えにくい領域にあります。だからこそ当協会は、技能の巧拙だけを語るのではなく、現場で何を守り、どの順で判断し、どこで立ち止まるかを基準として書き残します。実務基準は罰則集ではなく、主人の生活基盤を預かる者が同じ地図を見るための共通言語です。
              </p>
              <p>
                協会が実務基準を定めるのは、認定団体としての責任に根ざしています。依頼者にとっては、個人の人柄や経験年数だけに品質を委ねず、守られるべき線引きが可視化されることが安心の根拠になります。執事本人にとっても、その場その場の勘だけに判断を背負わせず、優先順位と境界を言葉で確認できる枠組みがあることで、判断負荷を軽減し、迷いを一人で抱え込まない支えとなります。基準は、依頼者保護と従事者保護の双方を同時に見据えた設計です。
              </p>
              <p>
                1890年（明治23年）の創立以来、協会は英国式の規律と日本の礼節を重ねる道を歩んできました。時代とともに住まい方や通信手段は変わりますが、先回り・沈黙・品位といった核は変わりません。基準は伝統を凍結するものではなく、現代の家庭運営でも説明可能な形へ翻訳し続けるための枠組みです。
              </p>
              <p>
                本ハブでは、十二の指針を一覧し、それぞれが職能のどの層を支えるかを示します。個別の料金や派遣の案内ではなく、認定団体として「現場の守り方」を定義する頁です。職能そのものの輪郭は
                <a href="/butler" className="text-[#1a2942] border-b border-[#a38e65] hover:text-[#a38e65] transition-colors mx-1">執事とは</a>
                を、学びの入口は
                <a href="/butler/become" className="text-[#1a2942] border-b border-[#a38e65] hover:text-[#a38e65] transition-colors mx-1">執事になるには</a>
                を併せて参照ください。
              </p>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">視点</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">認定団体</dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">構成</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">十二指針</dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">到達</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">三段階</dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">系譜</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
              </div>
            </dl>
          </section>

          {/* S2. 三つの柱 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Three Pillars</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              基準を支える<span className="text-[#a38e65]">三つの柱</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-9">
              十二の指針は並列のチェックリストではありません。倫理と行動を核に、目に見える奉仕の佇まい、そして家庭を預かる運営上の守り——この三層で読み解くと、現場での優先順位が見えやすくなります。柱は、基準を暗記するための分類ではなく、局面ごとに「いま何を最優先に据えるか」を見極めるための読み方です。
            </p>
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-8">
              {pillars.map((p) => (
                <div key={p.no} className="border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none block mb-3">{p.no}</span>
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5]">{p.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  柱と柱のあいだには重なりがあります。接遇の美しさも、守秘の徹底も、最終的には倫理に還ります。逆に、危機管理の手順だけを暗記しても、主人の尊厳を損なう動き方では基準を満たしたことになりません。各指針は独立して読める一方、相互参照を前提に設計しています。
                </p>
                <p>
                  代表理事・石井裕一（三代目頭取）のもと、協会は定義と実務を切り離さない運営を続けています。基準は机上の理想ではなく、研修と認定の現場で繰り返し参照される道具です。
                </p>
              </div>
              <div className="lg:pt-1">
                <picture>
                  <source srcSet="/images/generated/jba-butler-team-briefing.webp" type="image/webp" />
                  <img
                    src="/images/generated/jba-std-hub-02.jpg"
                    alt="実務基準を共有するチームブリーフィング。当日の段取りと優先順位を確認する様子"
                    className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                  />
                </picture>
              </div>
            </div>
          </section>

          {/* S3. 十二の実務基準一覧 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Twelve Standards</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              十二の<span className="text-[#a38e65]">実務基準</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-9">
              <p>
                以下は、当協会が定める実務基準の全体一覧です。各カードから個別指針へ進めます。ページは順次整備されますが、学びの順序と領域の地図として、いまこのハブから辿れるようにしています。
              </p>
              <p>
                十二領域は、読み手が全体像を掴みやすいよう、大きく四つの群として捉えることができます。第一に『人としての規律』——倫理綱領・行動規範・素養。第二に『技能としての作法』——接遇・所作・身だしなみ。第三に『情報と危機の管理』——守秘・危機管理・情報セキュリティ・時間管理。第四に『制度と将来』——認定基準、および執事とAIの役割分担。カードは番号順に並びますが、関心や現場の性質に応じて、いずれの群から入っても構いません。
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {standards.map((s) => (
                <a
                  key={s.no}
                  href={s.href}
                  className="group border border-[#e2ddd2] bg-white p-6 sm:p-7 shadow-[0_1px_0_rgba(26,41,66,0.04)] hover:border-[#a38e65]/60 transition-colors"
                >
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl leading-none">{s.no}</span>
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em]">{s.en}</span>
                  </div>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5] group-hover:text-[#a38e65] transition-colors">
                    {s.ja}
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.text}</p>
                  <span className="inline-block mt-4 font-['Noto_Serif_JP',serif] text-xs tracking-[0.14em] text-[#a38e65]">指針を見る →</span>
                </a>
              ))}
            </div>
            <p className="mt-9 font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              群としての整理は、指針の上下関係を固定するものではありません。たとえば守秘は『情報と危機の管理』に置きつつ、倫理の延長でもあります。一覧を俯瞰したうえで、関心の関心や担当現場の課題に近いカードから個別指針へ進んでください。
            </p>
          </section>

          {/* S4. 現場での用い方 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. In Practice</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              現場で<span className="text-[#a38e65]">基準を用いる</span>ということ
            </h2>
            <div className="grid lg:grid-cols-[320px_1fr] gap-8 lg:gap-12 items-start">
              <picture>
                <source srcSet="/images/generated/jba-butler-quality-control.webp" type="image/webp" />
                <img
                  src="/images/generated/jba-std-hub-03.jpg"
                  alt="実務基準に基づく品質確認。細部の点検と標準の徹底を示す場面"
                  className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/30"
                />
              </picture>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  基準は、朝の準備や来客前の確認、一日の終わりの振り返りで開かれることを想定しています。すべてを暗記してから現場に立つ必要はなく、局面に応じて該当する指針へ戻る使い方が本筋です。標準を身につけたうえで、主人の体調や季節、来客の性格に合わせて意図的に調整する——その往復が職能です。
                </p>
                <p>
                  また、基準は個人の気概だけに品質を背負わせません。手順の共有、上位水準者への相談、記録の残し方といった仕組みとセットで機能します。一人で抱え込まず、言葉にできる形へ落とすことが、家庭という閉じた空間での安全性を高めます。協会が基準を公開・共有する意義のひとつは、この「判断を一人に閉じない」状態をつくることにあります。
                </p>
                <p>
                  医療行為や法的代理、主人の意思決定そのものの代行は、いずれの指針にも含まれません。生活と対人サービスの領域で環境を整える——この外縁を自覚していること自体が、プロフェッショナルの条件です。
                </p>
              </div>
            </div>
          </section>

          {/* S5. 資格との関係 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Qualifications</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              資格制度と<span className="text-[#a38e65]">基準の対応</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                当協会の資格は、初級 Associate、中級 Professional、上級 Master の三段階に加え、特定領域を示す専門資格で構成されます。実務基準は、各段階が「何を判断し、何を実行できる状態か」を支える中身であり、称号のための飾りではありません。
              </p>
              <p>
                資格制度と実務基準は、到達を示す枠と、その中身を成す判断・振る舞いの関係にあります。Associate では倫理・行動・所作・身だしなみ・素養を中心に「職としての姿勢」を固め、Professional では接遇・守秘・時間管理・情報セキュリティを軸に家庭運営を再現可能な品質へ落とし、Master では危機管理や認定観点の理解、AIとの役割分担を含む広い判断と後進への伝達までを視野に入れます。
              </p>
              <p>
                段階が上がるほど参照する指針の幅と深さは増えますが、下位段階の基準が不要になるわけではなく、土台として積み重なる構造です。学びと認定は、この対応関係を往復することで一体となります。
              </p>
            </div>
            <div className="space-y-0">
              <article className="border-t border-[#e2ddd2]">
                <div className="bg-[#1a2942] px-5 sm:px-7 py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl sm:text-3xl leading-none tracking-[0.06em]">I</span>
                  <div className="w-px h-6 bg-[#a38e65]/50 hidden sm:block"></div>
                  <div>
                    <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-[12px] tracking-[0.12em]">Associate Butler</span>
                    <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.4]">アソシエイト・バトラー</h3>
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center bg-[#f8f6f0]/40">
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#a38e65] tracking-[0.04em] mb-3">姿勢・礼法・観察の土台</p>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] mb-6">
                      倫理・行動・所作・身だしなみ・素養の各指針が、この段階の中心になります。現場に入る前に「何を大切にする職か」を身体と意識の両面で整えるための基準です。人としての規律を言葉と所作に落とすことが、以降の技能習得の前提となります。
                    </p>
                    <a href="/qualifications" className={`${BTN08_ON_LIGHT} self-start`}>資格制度を見る</a>
                  </div>
                  <div className="min-h-[240px] lg:min-h-[300px]">
                    <picture>
                      <source srcSet="/images/generated/jba-become-foundation-training.webp" type="image/webp" />
                      <img
                        src="/images/gen2/std2-hub-associate-basic-training.jpg"
                        alt="アソシエイト段階で参照する基礎研修。講師と受講者が礼法と奉仕の基本を学ぶ場面"
                        className="w-full h-full min-h-[240px] lg:min-h-[300px] object-cover"
                      />
                    </picture>
                  </div>
                </div>
              </article>

              <article className="border-t border-[#e2ddd2]">
                <div className="bg-[#1a2942] px-5 sm:px-7 py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl sm:text-3xl leading-none tracking-[0.06em]">II</span>
                  <div className="w-px h-6 bg-[#a38e65]/50 hidden sm:block"></div>
                  <div>
                    <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-[12px] tracking-[0.12em]">Professional Butler</span>
                    <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.4]">プロフェッショナル・バトラー</h3>
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-0 lg:[&>div:first-child]:order-2">
                  <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center bg-[#f8f6f0]/40">
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#a38e65] tracking-[0.04em] mb-3">家庭運営を実務として回す水準</p>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] mb-6">
                      接遇・守秘・時間管理・情報セキュリティが比重を増します。状況判断のもとでサービスを設計し、再現可能な品質へ落とす段階です。技能としての作法と、情報・時間の守りを同時に回せるかが問われます。
                    </p>
                    <a href="/qualifications" className={`${BTN08_ON_LIGHT} self-start`}>到達水準を確認</a>
                  </div>
                  <div className="min-h-[240px] lg:min-h-[300px]">
                    <picture>
                      <source srcSet="/images/generated/jba-become-professional-practice.webp" type="image/webp" />
                      <img
                  src="/images/gen2/std2-hub-professional-household-practice.jpg"
                  alt="プロフェッショナル段階の実務。家庭運営を想定して寝室と予定を整える実践場面"
                        className="w-full h-full min-h-[240px] lg:min-h-[300px] object-cover"
                      />
                    </picture>
                  </div>
                </div>
              </article>

              <article className="border-t border-[#e2ddd2]">
                <div className="bg-[#1a2942] px-5 sm:px-7 py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl sm:text-3xl leading-none tracking-[0.06em]">III</span>
                  <div className="w-px h-6 bg-[#a38e65]/50 hidden sm:block"></div>
                  <div>
                    <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-[12px] tracking-[0.12em]">Master Butler</span>
                    <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.4]">マスター・バトラー</h3>
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center bg-[#f8f6f0]/40">
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#a38e65] tracking-[0.04em] mb-3">統括・育成・最終責任</p>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] mb-6">
                      危機管理、認定基準の理解、AIとの役割分担を含む広い判断が求められます。後進への基準の伝え方そのものが、この段階の実務です。制度と将来を見据え、現場の品質を個人技で終わらせない責任が加わります。
                    </p>
                    <a href="/standards/certification" className={`${BTN08_ON_LIGHT} self-start`}>認定基準を見る</a>
                  </div>
                  <div className="min-h-[240px] lg:min-h-[300px]">
                    <picture>
                      <source srcSet="/images/generated/jba-become-master-leadership.webp" type="image/webp" />
                      <img
                        src="/images/generated/jba-std-hub-06.jpg"
                        alt="マスター段階のリーダーシップ。後進指導と統括の姿勢を示す場面"
                        className="w-full h-full min-h-[240px] lg:min-h-[300px] object-cover"
                      />
                    </picture>
                  </div>
                </div>
              </article>
            </div>
          </section>

          {/* S6. 読み進め方 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. How to Read</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              基準の<span className="text-[#a38e65]">読み進め方</span>
            </h2>
            <div className="space-y-5">
              {readingPaths.map((r) => (
                <div key={r.title} className="bg-white border border-[#e2ddd2] p-6 sm:p-8 shadow-[0_1px_0_rgba(26,41,66,0.04)]">
                  <h3 className="jba-display font-medium text-[#1a2942] text-xl leading-[1.5] mb-3">{r.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">
                    {r.text}
                    <a href={r.linkHref} className="text-[#1a2942] border-b border-[#a38e65] hover:text-[#a38e65] transition-colors mx-1">{r.linkLabel}</a>
                    {r.suffix}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-10 border border-[#e2ddd2] bg-white p-6 sm:p-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">
                十二指針は、協会内の研修資料や認定の観点と整合するよう設計されています。公開頁では、依頼者・学び手の双方が期待値を合わせられる粒度で記述し、内部手順のすべてを開示するものではありません。それでも「どの領域に基準があるか」が可視化されること自体が、職能を社会と共有する第一歩だと考えます。読み進める際は、資格段階との対応や四つの群の整理を補助線にすると、自分に必要な指針へ辿り着きやすくなります。
              </p>
            </div>
          </section>

          {/* S7. FAQ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              実務基準に関する<span className="text-[#a38e65]">問いと答え</span>
            </h2>
            <div className="space-y-4">
              {faqJsonLd.map((item) => (
                <details key={item.question} className="group border border-[#e2ddd2] bg-white open:bg-[#f8f6f0]/35 transition-colors">
                  <summary className="cursor-pointer list-none flex items-start gap-4 p-5 sm:p-6 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.7]">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] shrink-0 mt-0.5">Q</span>
                    <span className="flex-1">{item.question}</span>
                    <span className="text-[#a38e65] shrink-0 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 pl-12 sm:pl-14">
                    <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-[15px] text-[#4a4638] leading-[1.95]">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* S8. CTA */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VIII. Next Steps</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              基準の先へ進む
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                実務基準の地図を手にしたら、倫理綱領から読み進めるか、関心のある領域の指針へ直接進むことができます。職能の定義、なり方、資格の到達水準は、いずれも基準と一続きです。必要に応じて関連頁へお進みください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 flex-wrap">
                <a href="/standards/ethics" className={BTN08_ON_NAVY}>倫理綱領から読む</a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度を見る</a>
                <a href="/butler" className={BTN08_ON_NAVY}>執事とは</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
